// Rides: is it working, and how far off Paris? Readiness against Day 1 first, then twelve
// weeks you can tap to see their rides, then the rides. A ride says how it felt (or asks),
// and stands against Day 1 as two bars. Everything is measured by Strava; Day 1 by Komoot.
import { useEffect, useRef, useState } from "preact/hooks";
import { useRoute } from "preact-iso";
import { useMusette, setFeel, rideDetail, rideMaps, pullRefresh } from "../lib/store";
import { RouteMap } from "../components/RouteMap";
import { RideGraphic } from "../components/RideGraphic";
import { decodePolyline } from "../lib/polyline";
import { Profile } from "../components/Profile";
import { Pull } from "../components/Pull";
import { Sheet } from "../components/Sheet";
import { dateLabel, dayLong, shortDate, todayIso, mondayOf, addDays, daysBetween } from "../lib/dates";
import { Head } from "../components/Head";
import { useToast } from "../components/Sheet";
import { longest, biggestClimb, real, weeklyKm } from "../lib/analysis";
import { FEELS } from "../lib/day";
import { photo } from "../data/photos";
import { fmtDur } from "./Today";
import { trends } from "../lib/trends";
import { NEW_GROUND } from "../lib/explore";
import type { Ride } from "../lib/types";

export function Rides() {
  const s = useMusette();
  const today = todayIso();
  const rides = s.rides.filter(real).slice().sort((a, b) => (a.d < b.d ? 1 : -1));
  const top = longest(rides), climb = biggestClimb(rides);
  const day1 = s.stages[0];
  const d1km = day1?.km.v ?? 207, d1m = day1?.gain.v ?? 1553;
  // Since the ramp began (26 October), with the weeks ahead as outlines and your summer's best
  // week as a faint line: forward, not a count of gaps.
  const rampStart = s.ramp[0]?.w ?? mondayOf(today);
  const sinceN = Math.max(1, Math.floor(daysBetween(rampStart, mondayOf(today)) / 7) + 1);
  const past = weeklyKm(rides, today, sinceN);
  const recentKm = (rides.filter((r) => r.km >= 15).slice(0, 8).reduce((a, r) => a + r.km, 0) / Math.max(1, Math.min(8, rides.filter((r) => r.km >= 15).length))) || 45;
  const ahead = s.ramp.filter((w) => w.w > mondayOf(today)).slice(0, Math.max(0, 8 - sinceN)).map((w) => ({ w: w.w, km: 0, rides: 0, target: w.km ?? (w.rides ?? 3) * recentKm, ahead: true }));
  const weeks = [...past.map((w) => ({ ...w, target: (() => { const r = s.ramp.find((x) => x.w === w.w); return r ? r.km ?? (r.rides ?? 3) * recentKm : 0; })(), ahead: false })), ...ahead];
  const summer = weeklyKm(rides.filter((r) => r.d < rampStart), rampStart, 26).reduce((m, w) => Math.max(m, w.km), 0);
  const maxW = Math.max(1, summer, ...weeks.map((w) => Math.max(w.km, w.target)));
  const [pick, setPick] = useState<string | null>(null);
  const [maps, setMaps] = useState<Record<string, string>>({});
  useEffect(() => { rideMaps().then(setMaps); }, [s.rides.length]);
  const [peek, setPeek] = useState<Ride | null>(null);
  const shown = pick ? rides.filter((r) => r.d >= pick && r.d <= addDays(pick, 6)) : rides;
  const picked = weeks.find((w) => w.w === pick);
  const toGo = day1 ? daysBetween(today, day1.date) : null;
  const tr = trends(s.rides, today, rampStart);

  return (
    <main class="screen signal">
      <Head right={`${rides.length} rides`} />
      <Pull onRefresh={pullRefresh} />

      <section class="stack" style="margin-top: var(--space-5)">
        <a class="card ready-card" href="/paris" aria-label="Open Paris: Day 1">
          <div class="row-between"><span class="label amber">Paris · Day 1</span><span class="label">{toGo != null ? `${Math.max(0, Math.floor(toGo / 7))} weeks` : ""}</span></div>
          <div class="ready-big"><span class="hero-n sm">{top ? top.km : 0}</span><span class="soft">of {d1km} km</span></div>
          <Track value={top?.km ?? 0} of={d1km} marks={[100, 150]} label="Longest ride against Day 1's distance" />
          <div class="row-between"><span class="label">Climb</span><span class="label">{climb ? climb.m : 0} of {d1m.toLocaleString("en-GB")} m</span></div>
          <Track value={climb?.m ?? 0} of={d1m} thin label="Biggest climb against Day 1's climb" />
          <span class="soft">Manchester to Wellingborough, {dateLabel(day1?.date ?? "2027-06-12")}. Longest and biggest climb measured by Strava; Day 1 by Komoot.</span>
          <span class="label amber">Open Paris</span>
        </a>

        <div class="card">
          <div class="row-between">
            <span class="label ink">{picked ? `Week of ${shortDate(picked.w)}` : "Since the plan began"}</span>
            <span class="label">{picked ? `${picked.km} km · ${picked.rides} ${picked.rides === 1 ? "ride" : "rides"}` : `${Math.round(past.reduce((a, w) => a + w.km, 0))} km`}</span>
          </div>
          <div class="weeks" role="radiogroup" aria-label="Kilometres a week" style={`grid-template-columns: repeat(${weeks.length}, minmax(0, 1fr))`}>
            {summer > 0 && <span class="weeks-best" style={`bottom:${(summer / maxW) * 100}%`} aria-hidden="true" />}
            {weeks.map((w) => (
              <button type="button" role="radio" aria-checked={w.w === pick} disabled={w.ahead} class={`${w.w === pick ? "on" : ""}${w.w === mondayOf(today) ? " now" : ""}${w.ahead ? " ahead" : ""}`}
                style={`--h:${Math.max(3, Math.round((w.km / maxW) * 100))}%;--t:${Math.round((w.target / maxW) * 100)}%`} onClick={() => setPick(w.w === pick ? null : w.w)} aria-label={`Week of ${shortDate(w.w)}: ${w.ahead ? `target ${Math.round(w.target)} km` : `${w.km} km`}`}>
                <i class="wk-target" /><span />
              </button>
            ))}
          </div>
          <div class="row-between"><span class="label">{shortDate(rampStart)}</span><span class="label">{pick ? "Tap again for all" : summer ? `Dashed: your summer's best week, ${Math.round(summer)} km` : "Outlines: the ramp ahead"}</span></div>
        </div>

        <div class="card">
          <div class="row-between"><span class="label ink">Trends</span><span class="label">Last four weeks</span></div>
          {tr.ready ? (
            <div class="trends">
              {tr.items.map((t) => (
                <div class="trend" aria-label={`${t.label}: ${t.now}, ${t.dir === "flat" ? "level" : t.dir}. ${t.note}.`}>
                  <span class="label">{t.label}</span>
                  <span class="trend-n">{t.now}<i class={`tr-arrow ${t.dir}${t.good === true ? " good" : t.good === false ? " bad" : ""}`} aria-hidden="true">{t.dir === "up" ? "↑" : t.dir === "down" ? "↓" : "→"}</i></span>
                  <span class="label">{t.note}</span>
                </div>
              ))}
            </div>
          ) : <span class="soft">Trends start {dayLong(tr.from)} {shortDate(tr.from)}, once the plan has two weeks in it. Strava data only.</span>}
          {tr.ready && <span class="label">Measured by Strava. Speed per heartbeat is km/h for each beat a minute, times 100: up means the same work costs less heart.</span>}
        </div>

        <a class="card row-between" href="/routes"><span class="label ink">The route library</span><span class="label">{s.routes.length} routes</span></a>
        <a class="card row-between" href="/routes/new"><span class="label ink">New ground from Komoot</span><span class="label amber">{(() => { const n = NEW_GROUND - Object.keys(s.log?.explore ?? {}).length - s.routes.filter((r) => /^[kps]-/.test(r.id)).length; return n > 0 ? `${n} to review` : "Reviewed"; })()}</span></a>

        {shown.map((r) => <RideRow r={r} top={r === top} climb={r === climb} feel={s.log?.feel?.[r.d]} poly={r.id ? maps[r.id] : undefined} onPeek={() => setPeek(r)} />)}
        {!shown.length && <span class="soft">{pick ? "No rides that week." : "No rides yet. Strava syncs every hour."}</span>}
      </section>
      <Sheet open={!!peek} onClose={() => setPeek(null)} label="Ride preview">{peek && <Peek r={peek} poly={peek.id ? maps[peek.id] : undefined} feel={s.log?.feel?.[peek.d]} />}</Sheet>
    </main>
  );
}

/** Press and hold a ride: its map, how it felt, and the write-up's headline, without leaving the list. */
function Peek({ r, poly, feel }: { r: Ride; poly?: string; feel?: string }) {
  const [d, setD] = useState<any>(null);
  useEffect(() => { if (r.id) rideDetail(r.id).then(setD); }, [r.id]);
  return (
    <div class="sheet-stack">
      <span class="label">{dateLabel(r.d)}{feel ? ` · ${FEELS.find((f) => f.id === feel)?.label}` : ""}</span>
      <span class="say">{d?.writeup?.headline ?? r.name ?? `${r.km} km`}</span>
      {(poly || d?.poly) && <RouteMap poly={poly ?? d.poly} label={`Map of ${r.name ?? "the ride"}`} h={200} tiles />}
      <span class="soft">{d?.writeup?.text ?? `${r.km} km, ${r.m} m${r.hr ? `, average heart rate ${r.hr}` : ""}.`}</span>
      <a class="pill wide" href={`/rides/${r.id ?? r.d}`}>Open the ride</a>
    </div>
  );
}

function RideRow({ r, top, climb, feel, poly, onPeek }: { r: Ride; top: boolean; climb: boolean; feel?: string; poly?: string; onPeek: () => void }) {
  const tag = top && climb ? "Longest · Biggest climb" : top ? "Longest" : climb ? "Biggest climb" : null;
  const timer = useRef<number | null>(null);
  const held = useRef(false);
  const press = () => { held.current = false; timer.current = window.setTimeout(() => { held.current = true; onPeek(); }, 450); };
  const release = () => { if (timer.current) clearTimeout(timer.current); };
  return (
    <a class={`card ride-row${r.photo || poly ? " has-photo" : ""}`} href={`/rides/${r.id ?? r.d}`}
      onPointerDown={press} onPointerUp={release} onPointerLeave={release} onPointerCancel={release}
      onClick={(e) => { if (held.current) { e.preventDefault(); held.current = false; } }} onContextMenu={(e) => e.preventDefault()}>
      {r.photo ? <img src={r.photo} alt="" loading="lazy" decoding="async" /> : poly ? <RouteMap poly={poly} label="" thumb /> : null}
      <div class="ride-row-text">
        <div class="row-between"><span class="label">{dateLabel(r.d)}</span>{tag && <span class="label amber">{tag}</span>}</div>
        <span class="stat-n">{r.km} km <span class="soft">· {r.m} m</span></span>
        <span class="soft">{[r.name, feel ? FEELS.find((f) => f.id === feel)?.label : null].filter(Boolean).join(" · ")}</span>
      </div>
    </a>
  );
}

function Track({ value, of, marks = [], thin, label }: { value: number; of: number; marks?: number[]; thin?: boolean; label: string }) {
  const p = Math.min(100, (value / of) * 100);
  return (
    <div class={`track${thin ? " thin" : ""}`} role="img" aria-label={`${label}: ${value} of ${of}`}>
      <span class="track-fill" style={`width:${p}%`} />
      {marks.map((m) => <span class="track-mark" style={`left:${(m / of) * 100}%`}><span class="label">{m}</span></span>)}
      <span class="track-dot" style={`left:${p}%`} />
    </div>
  );
}

export function RideCard() {
  const s = useMusette();
  const { params } = useRoute();
  const { show, node: toast } = useToast();
  const rides = s.rides.filter(real).slice().sort((a, b) => (a.d < b.d ? 1 : -1));
  const r = rides.find((x) => String(x.id) === params.id) ?? rides.find((x) => x.d === params.id) ?? rides[Number(params.id)];
  if (!r) return <main class="screen signal"><Head right="Rides" /><div class="stack" style="margin-top:var(--space-6)"><span class="soft">That ride is not here.</span><a class="text-btn" href="/rides">All rides</a></div></main>;
  const top = longest(rides), climb = biggestClimb(rides);
  const day1 = s.stages[0];
  const d1km = day1?.km.v ?? 207, d1m = day1?.gain.v ?? 1553;
  const kmh = r.s > 0 ? Math.round((r.km / (r.s / 3600)) * 10) / 10 : null;
  const sentence = r === climb && r === top ? `Your longest ride and biggest climb, of ${rides.length}.` : r === climb ? `Your biggest climbing day of ${rides.length} rides.` : r === top ? `Your longest ride of ${rides.length}.` : `${Math.round((r.km / (top?.km ?? r.km)) * 100)}% of your longest ride.`;
  const feel = s.log?.feel?.[r.d];
  return <RideCardBody r={r} rides={rides} top={top} climb={climb} d1km={d1km} d1m={d1m} kmh={kmh} sentence={sentence} feel={feel} show={show} toast={toast} />;
}

function RideCardBody({ r, rides, d1km, d1m, kmh, sentence, feel, show, toast }: { r: Ride; rides: Ride[]; top: Ride | null; climb: Ride | null; d1km: number; d1m: number; kmh: number | null; sentence: string; feel?: string; show: (t: string, u?: () => void) => void; toast: any }) {
  const [d, setD] = useState<any>(undefined);
  const [sharing, setSharing] = useState(false);
  const home = useMusette().settings?.home;
  useEffect(() => { if (r.id) rideDetail(r.id).then(setD); else setD(null); }, [r.id]);
  const fx = d?.facts;
  return (
    <main class="screen signal">
      <Head right={dateLabel(r.d)} />
      <section class="stack" style="margin-top: var(--space-3)">
        <a href="/rides" class="label back">← Rides</a>
        <div class="card photo-card tall">
          <img src={r.photo ?? photo("routes")} alt="" decoding="async" />
          <div class="photo-cap"><span class="label" style="color:#fff">{r.name ?? dayLong(r.d)} · Measured by Strava</span></div>
        </div>
      </section>
      <section class="stack" style="margin-top: var(--space-5); gap: var(--space-2)">
        <div class="editor-time"><span class="hero-n">{r.km}</span><span class="editor-to"><span class="label">km</span><span class="stat-n">{r.m} m</span></span></div>
        <span class="lede-s">{sentence}</span>
      </section>
      <section class="stack" style="margin-top: var(--space-5)">
        {d?.writeup ? (
          <div class="card coach-card">
            <span class="label amber">The write-up</span>
            <span class="say">{d.writeup.headline}</span>
            <span class="soft prose-s">{d.writeup.text}</span>
            <span class="label">Written by the coach from the facts below. Measured by Strava, computed by Musette.</span>
          </div>
        ) : d && r.d >= "2026-01-01" ? (
          <div class="card"><span class="label">The write-up</span><span class="soft">Arrives once the live key is in. The facts are below.</span></div>
        ) : null}
        {d?.poly && <div class="card map-card"><RouteMap poly={d.poly} label={`Map of ${r.name ?? "the ride"}`} h={240} tiles /><div class="row-between">{d.far != null ? <span class="label">Furthest from home {d.far} km</span> : <span />}<button type="button" class="pill small quiet-pill" onClick={() => setSharing(true)}>Share</button></div></div>}
        {d?.poly && <RideGraphic open={sharing} onClose={() => setSharing(false)} r={r} pts={decodePolyline(d.poly)} home={home} profile={d.profile} />}
        {d?.profile && <div class="card"><span class="label ink">Height</span><Profile points={d.profile} label={`Height profile, ${r.m} m of climbing`} /></div>}
        {d === undefined && r.id && <div class="card"><span class="soft">Loading the map and the facts.</span></div>}
        {d === null && r.id && <div class="card"><span class="soft">The map, height and facts are built within 15 minutes of a ride reaching Strava.</span></div>}
        <div class="card">
          <span class="label ink">How it felt</span>
          <div class="chips feel-strip" role="radiogroup" aria-label="How it felt">
            {FEELS.map((f) => <button type="button" role="radio" aria-checked={feel === f.id} class={`chip${feel === f.id ? " on" : ""}`} onClick={() => { const was = feel; setFeel(r.d, feel === f.id ? null : f.id); show(feel === f.id ? "Cleared" : `Logged: ${f.label.toLowerCase()}`, () => setFeel(r.d, (was as any) ?? null)); }}><span>{f.label}</span></button>)}
          </div>
          <span class="soft">{feel ? "The coach reads this with the numbers." : "One tap. The coach reads it with the numbers."}</span>
        </div>
        <div class="stats">
          <div class="card stat"><span class="label">Moving</span><span class="stat-n">{r.s > 0 ? fmtDur(r.s / 60).replace(" h ", ":") : "–"}</span></div>
          <div class="card stat"><span class="label">km/h</span><span class="stat-n">{kmh ?? "–"}</span></div>
          <div class="card stat"><span class="label">m per km</span><span class="stat-n">{r.km ? Math.round((r.m / r.km) * 10) / 10 : "–"}</span></div>
        </div>
        {fx && (fx.hr || fx.power || fx.weather) && (
          <div class="stats">
            {fx.hr && <div class="card stat"><span class="label">Heart rate</span><span class="stat-n">{fx.hr.avg}<small> avg</small></span></div>}
            {fx.power ? <div class="card stat"><span class="label">Power</span><span class="stat-n">{fx.power.np ?? fx.power.avg}<small> W</small></span></div> : fx.re ? <div class="card stat"><span class="label">Effort</span><span class="stat-n">{fx.re}</span></div> : null}
            {fx.weather && <div class="card stat"><span class="label">Feels</span><span class="stat-n">{fx.weather.feels}°<small> {fx.weather.wind} km/h</small></span></div>}
          </div>
        )}
        {fx && (fx.efficiency || fx.drift != null) && (
          <div class="card">
            <span class="label ink">Getting stronger?</span>
            {fx.efficiency && <div class="row-between"><span class="note-title">Metres per heartbeat</span><span class="label">{fx.efficiency.mPerBeat}{fx.efficiency.vsRecent != null ? ` · ${fx.efficiency.vsRecent > 0 ? "+" : ""}${fx.efficiency.vsRecent}% on similar rides` : ""}</span></div>}
            {fx.drift != null && <div class="row-between"><span class="note-title">Heart-rate drift</span><span class="label">{fx.drift}%{fx.drift < 5 ? " · steady" : ""}</span></div>}
            <span class="soft">{fx.efficiency?.vsRecent != null ? `Against the median of your last ${fx.efficiency.against} rides on similar terrain. Higher means more distance for the same effort.` : "Each ride is measured against your recent rides on similar terrain once there are two to compare."} Computed by Musette from Strava's streams.</span>
          </div>
        )}
        {fx?.climbs?.length > 0 && (
          <div class="card">
            <span class="label ink">Climbs</span>
            {fx.climbs.map((c: any) => <div class="row-between"><span class="note-title">{c.name}</span><span class={`label${c.pr || (c.vsBest != null && c.vsBest < 0) ? " amber" : ""}`}>{c.time}{c.pr ? " · PR" : c.vsBest != null ? ` · ${c.vsBest > 0 ? "+" : "−"}${Math.abs(c.vsBest)} s on your best` : ""}</span></div>)}
            <span class="label">{fx.climbs.length} {fx.climbs.length === 1 ? "climb" : "climbs"} of 3% or more, from Strava's segments.</span>
          </div>
        )}
        {fx?.notable?.length > 0 && (
          <div class="card">
            <span class="label ink">Notable</span>
            {fx.notable.map((n: string) => <span class="soft">{n}.</span>)}
          </div>
        )}
        <div class="card">
          <div class="row-between"><span class="label ink">Against Day 1</span><span class="label">{d1km} km · {d1m.toLocaleString("en-GB")} m</span></div>
          <div class="row-between"><span class="label">Distance</span><span class="label">{r.km} of {d1km}</span></div>
          <Track value={r.km} of={d1km} thin label="Distance against Day 1" />
          <div class="row-between"><span class="label">Climb</span><span class="label">{r.m} of {d1m.toLocaleString("en-GB")}</span></div>
          <Track value={r.m} of={d1m} thin label="Climb against Day 1" />
        </div>
        {r.id && <a class="text-btn" href={`https://www.strava.com/activities/${r.id}`} target="_blank" rel="noopener">Open on Strava</a>}
      </section>
      <div class="dock-wrap">{toast}</div>
    </main>
  );
}
